/**
 * App-root banner — pinned at the top of every page.
 *
 * Two visual modes (Issue #20):
 *
 *   1. **dev mode (default)** — "Dev tool only" disclaimer (ADR-007
 *      §Mitigation §1). The emulator auto-signs with canonical dev keys
 *      and bypasses TLS — making it attractive for "quick prod checks".
 *      Banner prevents that drift. Plus version chip + target Gateway.
 *
 *   2. **business mode** — «Демо-стенд Nikira DLT» — friendly banner для
 *      бизнес-аудитории. Без `request_id` / Datadog ссылок / технических
 *      терминов. Toggle «Режим разработчика» в правом верхнем углу
 *      возвращает обратно в dev mode.
 *
 * Both modes carry the live Gateway URL — without it a switch between
 * local and CI Gateway is invisible to the user.
 */
import { useEffect, useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

interface VersionResponse {
  emulator: string;
  target_gateway: string;
  node: string;
  platform: string;
}

interface HeaderBannerProps {
  readonly mode: 'dev' | 'business';
  readonly onSwitchMode: (next: 'dev' | 'business') => void;
}

export function HeaderBanner({ mode, onSwitchMode }: HeaderBannerProps) {
  const [version, setVersion] = useState<VersionResponse | null>(null);
  const [versionErr, setVersionErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetch('/api/version')
      .then(async (r) => {
        if (!r.ok) throw new Error(`/api/version HTTP ${r.status}`);
        return (await r.json()) as VersionResponse;
      })
      .then((v) => {
        if (!cancelled) setVersion(v);
      })
      .catch((e: unknown) => {
        if (!cancelled) setVersionErr((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (mode === 'business') {
    return (
      <header className="border-b border-slate-200 bg-white">
        <div className="px-6 py-4 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-slate-900">Демо-стенд Nikira DLT</span>
            <Badge variant="info">бизнес-демо</Badge>
          </div>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-600">
            интерактивный показ создания договоров в распределённом реестре
          </span>
          <div className="flex-1" />
          {version !== null && (
            <span className="text-xs text-slate-500 font-mono hidden sm:inline">
              реестр: <span className="text-slate-700">{version.target_gateway}</span>
            </span>
          )}
          <a
            href="/portal"
            className="inline-flex items-center px-3 py-1.5 text-sm rounded text-slate-700 hover:bg-slate-100 transition-colors"
            title="Developer Portal — API docs, SDKs, webhook stream"
          >
            Портал интегратора →
          </a>
          <Button size="sm" variant="ghost" onClick={() => onSwitchMode('dev')}>
            Режим разработчика
          </Button>
        </div>
      </header>
    );
  }

  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-900 text-xs text-center">
        <strong>Dev tool only.</strong> Production debugging happens via{' '}
        <code className="font-mono">request_id</code> tracing in Datadog &amp; Gateway logs — never
        point this emulator at production OP credentials. See{' '}
        <code className="font-mono">docs/adr/ADR-007</code>.
      </div>
      <div className="px-6 py-3 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold">Nikira DLT</span>
          <Badge variant="info">OP Emulator</Badge>
        </div>
        <span className="text-slate-300">|</span>
        <span className="text-sm text-slate-600">Day 3 MVP</span>
        <nav className="flex items-center gap-1 ml-2 text-xs">
          <a
            href="/portal"
            className="px-2 py-1 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            title="Developer Portal landing"
          >
            Portal
          </a>
          <a
            href="/docs"
            className="px-2 py-1 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            title="Swagger UI — all 17 Gateway endpoints"
          >
            API Docs
          </a>
          <a
            href="/portal/sdks"
            className="px-2 py-1 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            title="SDKs, Postman collection, sandbox credentials"
          >
            SDKs
          </a>
          <a
            href="/portal/webhooks"
            className="px-2 py-1 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            title="Live webhook stream + event catalog"
          >
            Webhooks
          </a>
        </nav>
        <div className="flex-1" />
        {version ? (
          <div className="flex items-center gap-3 text-xs font-mono text-slate-600">
            <span>
              emulator <span className="text-slate-900">{version.emulator}</span>
            </span>
            <span className="text-slate-300">·</span>
            <span>
              target <span className="text-slate-900">{version.target_gateway}</span>
            </span>
            <span className="text-slate-300">·</span>
            <span>
              node <span className="text-slate-900">{version.node}</span>
            </span>
          </div>
        ) : versionErr ? (
          <Badge variant="danger" title={versionErr}>
            emulator backend unreachable
          </Badge>
        ) : (
          <span className="text-xs text-slate-400">loading …</span>
        )}
        <Button size="sm" variant="ghost" onClick={() => onSwitchMode('business')}>
          Бизнес-демо →
        </Button>
      </div>
    </header>
  );
}
